import otWhiteboard from '@aha-app/ot-whiteboard';
import { cloneDeep } from 'lodash';
import { type OtWhiteboardOp } from '../ot-whiteboard';
import type ClientBase from './ClientBase';

// An entry in the undo or redo stacks of the UndoManager.
class UndoEntry {
  _entryOps: OtWhiteboardOp[];
  _dirty: boolean = false;

  constructor(op: OtWhiteboardOp[] = []) {
    // New entries are always ready for undo, so store the inverted operations.
    this.entryOps = otWhiteboard.type.invert(op);
  }

  get entryOps() {
    // when accessing entryOps, we compact operations just-in-time if needed
    if (this._dirty) {
      this._entryOps = otWhiteboard.type.compose([], this._entryOps);
      this._dirty = false;
    }
    return this._entryOps;
  }

  set entryOps(ops: OtWhiteboardOp[]) {
    this._entryOps = ops;
    this._dirty = true;
  }

  // Check if the undo entry contains any operations.
  empty() {
    return this.entryOps.length === 0;
  }

  // Apply the operations in this entry.
  apply(client: ClientBase) {
    client.applyRemoteOperations(cloneDeep(this.entryOps), {
      isSource: true,
    });
    client.sendOps(this.entryOps);
  }

  // Invert the operations (to make an undo entry into a redo entry)
  reverse() {
    this.entryOps = otWhiteboard.type.invert(this.entryOps);
    return this;
  }

  // Add an array of operations to the entry.
  addOperations(op: OtWhiteboardOp[]) {
    this.entryOps = otWhiteboard.type.invert(op).concat(
      // direct access because we don't want compose to be called here
      this._entryOps
    );
  }

  // Transform the entry by the remote operation.
  transform(remoteOp: OtWhiteboardOp[]) {
    const [left, right] = otWhiteboard.type.transformX(this.entryOps, remoteOp);
    this.entryOps = left;

    // Return the transformed remote operations.
    return right;
  }
}

export default UndoEntry;
